// Theme and background utility functions for space pages

import Image from "next/image";

// Helper function to safely get media URL
function safeGetMediaUrl(media) {
  if (typeof media === "string" && media.trim() !== "") {
    return media;
  }
  if (
    media &&
    typeof media === "object" &&
    media.url &&
    typeof media.url === "string"
  ) {
    return media.url;
  }
  return null;
}

/**
 * Calculates average color from an image URL
 * @param {string} imageUrl - URL of the image to analyze
 * @param {Function} setAverageBackgroundColor - State setter function
 */
export const calculateAverageColor = (imageUrl, setAverageBackgroundColor) => {
  // Check if we're in the browser environment
  if (typeof window === "undefined") return;

  // Validate imageUrl is a string
  const validImageUrl = safeGetMediaUrl(imageUrl);
  if (!validImageUrl) {
    console.warn(
      "Invalid image URL provided to calculateAverageColor:",
      imageUrl
    );
    setAverageBackgroundColor(null);
    return;
  }

  const img = new window.Image();
  img.crossOrigin = "Anonymous";

  img.onload = () => {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    canvas.width = img.width;
    canvas.height = img.height;

    ctx.drawImage(img, 0, 0);
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height).data;

    let r = 0,
      g = 0,
      b = 0,
      count = 0;

    // Sample pixels (every 10th pixel for performance)
    for (let i = 0; i < imageData.length; i += 40) {
      r += imageData[i];
      g += imageData[i + 1];
      b += imageData[i + 2];
      count++;
    }

    r = r / count / 255;
    g = g / count / 255;
    b = b / count / 255;

    setAverageBackgroundColor({ r, g, b });
  };

  img.onerror = () => {
    console.error("Error loading image for color calculation");
    setAverageBackgroundColor(null);
  };

  img.src = validImageUrl;
};

/**
 * Determines if the background is light or dark
 * @param {Object} spaceData - Space data containing theme information
 * @param {Object|null} averageBackgroundColor - Average RGB values of background image
 * @returns {boolean} - True if background is light, false if dark
 */
export const isLightBackground = (spaceData, averageBackgroundColor) => {
  const background = spaceData?.space_data?.theme?.background;
  if (!background) return false;

  if (background.type === "color") {
    const hex = background.color.replace("#", "");
    const r = parseInt(hex.substr(0, 2), 16) / 255;
    const g = parseInt(hex.substr(2, 2), 16) / 255;
    const b = parseInt(hex.substr(4, 2), 16) / 255;
    const luminance = 0.299 * r + 0.587 * g + 0.114 * b;
    return luminance > 0.5;
  }

  if (background.type === "image" && averageBackgroundColor) {
    const { r, g, b } = averageBackgroundColor;
    const luminance = 0.299 * r + 0.587 * g + 0.114 * b;
    return luminance > 0.5;
  }

  return false;
};

/**
 * Gets header button styles based on background
 * @param {Object} spaceData - Space data containing theme information
 * @param {Object|null} averageBackgroundColor - Average RGB values of background image
 * @returns {Object} - Button style object
 */
export const getHeaderButtonStyles = (spaceData, averageBackgroundColor) => {
  const isLight = isLightBackground(spaceData, averageBackgroundColor);

  return {
    backgroundColor: isLight
      ? "rgba(0, 0, 0, 0.1)"
      : "rgba(255, 255, 255, 0.1)",
    color: isLight ? "#000000" : "#ffffff",
    border: `1px solid ${isLight ? "rgba(0, 0, 0, 0.2)" : "rgba(255, 255, 255, 0.2)"}`,
  };
};

/**
 * Renders background media elements for a space
 * @param {Object} spaceData - Space data containing theme and media information
 * @param {boolean} isOwner - Whether current user is the owner of the space
 * @param {boolean} isEditMode - Whether the space is in edit mode
 * @param {string|null} previewMode - Current preview mode (desktop, tablet, mobile)
 * @returns {JSX.Element|null} - Background media elements or null
 */
export const renderBackgroundMedia = (
  spaceData,
  isOwner,
  isEditMode,
  previewMode
) => {
  const background = spaceData?.space_data?.theme?.background;
  if (!background) return null;

  const {
    type,
    media,
    media_credit,
    color,
    overlay_gradient: overlayGradient,
    overlay_opacity: overlayOpacity = 0.5,
  } = background;

  // Safely get media URL
  const mediaUrl = safeGetMediaUrl(media);

  // Return early if it's just a color background
  if (type === "color" && !overlayGradient) return null;

  // Don't render background media when in preview mode
  if (isOwner && isEditMode && previewMode) return null;

  return (
    <>
      {/* Media Layer */}
      {type === "video" && mediaUrl && (
        <video
          autoPlay
          loop
          muted
          playsInline
          className="fixed inset-0 w-full h-full object-cover -z-20"
        >
          <source src={mediaUrl} type="video/mp4" />
        </video>
      )}
      {type === "image" && mediaUrl && (
        <>
          <div className="fixed inset-0 w-full h-full -z-20">
            <Image
              src={mediaUrl}
              alt="Background"
              fill
              priority
              sizes="100vw"
              className="object-cover"
              unoptimized={
                mediaUrl.startsWith("https://") && !mediaUrl.includes("qensa")
              }
            />
          </div>
          {media_credit && (
            <div className="fixed bottom-4 left-4 text-xs text-white/60 z-10">
              Photo by{" "}
              <a
                href={
                  media_credit.profile_url +
                  "?utm_source=qensa&utm_medium=referral"
                }
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-white/80 underline transition-colors"
              >
                {media_credit.name}
              </a>{" "}
              on{" "}
              <a
                href="https://unsplash.com?utm_source=qensa&utm_medium=referral"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-white/80 underline transition-colors"
              >
                Unsplash
              </a>
            </div>
          )}
        </>
      )}

      {/* Overlay Layer */}
      {overlayGradient && (
        <div
          className="fixed inset-0 w-full h-full -z-10"
          style={{
            background: overlayGradient,
            opacity: overlayOpacity,
          }}
        />
      )}
    </>
  );
};

/**
 * Renders background media for preview frames
 * @param {Object} spaceData - Space data containing theme and media information
 * @returns {JSX.Element|null} - Background media elements for preview or null
 */
export const renderPreviewBackgroundMedia = (spaceData) => {
  const background = spaceData?.space_data?.theme?.background;
  if (!background) return null;

  const {
    type,
    media,
    media_credit,
    color,
    overlay_gradient: overlayGradient,
    overlay_opacity: overlayOpacity = 0.5,
  } = background;

  // Safely get media URL
  const mediaUrl = safeGetMediaUrl(media);

  // Return early if it's just a color background
  if (type === "color" && !overlayGradient) return null;

  return (
    <>
      {/* Media Layer */}
      {type === "video" && mediaUrl && (
        <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
          style={{ zIndex: 1 }}
        >
          <source src={mediaUrl} type="video/mp4" />
        </video>
      )}
      {type === "image" && mediaUrl && (
        <div className="absolute inset-0 w-full h-full" style={{ zIndex: 1 }}>
          <Image
            src={mediaUrl}
            alt="Background"
            fill
            priority
            sizes="100vw"
            className="object-cover"
            unoptimized={
              mediaUrl.startsWith("https://") && !mediaUrl.includes("qensa")
            }
          />
        </div>
      )}

      {/* Overlay Layer */}
      {overlayGradient && (
        <div
          className="absolute inset-0 w-full h-full"
          style={{
            background: overlayGradient,
            opacity: overlayOpacity,
            zIndex: 2,
          }}
        />
      )}
    </>
  );
};

/**
 * Gets the background color for preview frames
 * @param {Object} spaceData - Space data containing theme information
 * @returns {string} - Background color or 'transparent'
 */
export const getPreviewBackgroundColor = (spaceData) => {
  const background = spaceData?.space_data?.theme?.background;
  if (!background) return "#3D0CF5"; // Default fallback

  if (background.type === "color") {
    return background.color;
  }

  // For image or video, return transparent to show the media
  return "transparent";
};

/**
 * Gets page styles with current background settings
 * @param {Object} spaceData - Space data containing theme information
 * @param {boolean} isOwner - Whether current user is the owner of the space
 * @param {boolean} isEditMode - Whether the space is in edit mode
 * @param {string|null} previewMode - Current preview mode (desktop, tablet, mobile)
 * @returns {Object} - Page style object
 */
export const getPageStyles = (spaceData, isOwner, isEditMode, previewMode) => {
  return {
    // Use CSS custom property for better iOS viewport height handling
    minHeight: "var(--dynamic-vh, 100vh)",
    display: "flex",
    flexDirection: "column",
    background: (() => {
      // If in preview mode, use a white background for the main page
      if (isOwner && isEditMode && previewMode) {
        return "#ffffff";
      }

      const background = spaceData?.space_data?.theme?.background;
      if (!background) return "#3D0CF5"; // Default fallback

      // Only return the color if it's a color type background
      if (background.type === "color") {
        return background.color;
      }

      // For image or video types, return transparent to show the media layer
      return "transparent";
    })(),
    position: "relative",
    overflowX: "hidden", // Hide horizontal overflow
    overflowY: "auto", // Allow vertical scrolling
  };
};

/**
 * Gets theme styles from space data
 * @param {Object} spaceData - Space data containing theme information
 * @returns {Object} - CSS variables and theme properties
 */
export const getThemeStyles = (spaceData) => {
  return {
    fontFamily: spaceData?.space_data?.theme?.fonts?.body,
    "--primary-color": spaceData?.space_data?.theme?.colors?.primary,
    "--text-color": spaceData?.space_data?.theme?.colors?.text,
    "--secondary-color": spaceData?.space_data?.theme?.colors?.secondary,
    "--spacing": spaceData?.space_data?.theme?.layout?.spacing || "1rem",
  };
};
